import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LocalDate } from "@/components/ui/local-date";
import { ApiError, api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { createFileRoute, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { z } from "zod";

/**
 * `/accounting/journal` — the journal and the trial balance.
 *
 * The journal is evidence: entries are neither edited nor deleted here,
 * only reversed — and the reversal stays visible in the list next to what
 * opravovalo.
 */

const searchSchema = z.object({ year: z.number().optional() });

type Line = {
  id: string;
  amount: string;
  description: string | null;
  debitAccount: { code: string; name: string };
  creditAccount: { code: string; name: string };
};

type Entry = {
  id: string;
  number: number;
  bookedOn: string;
  description: string;
  reversesId: string | null;
  lines: Line[];
};

type TrialRow = {
  code: string;
  name: string;
  debit: string;
  credit: string;
  balance: string;
};

export const Route = createFileRoute("/_app/accounting/journal")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ year: search.year }),
  loader: async ({ deps }) => {
    const query = deps.year ? `?year=${deps.year}` : "";
    try {
      const [journal, balance] = await Promise.all([
        api.get<{ year: number; closed: boolean; entries: Entry[] }>(
          `/api/accounting/journal${query}`,
        ),
        api.get<{
          rows: TrialRow[];
          totalDebit: string;
          totalCredit: string;
          balanced: boolean;
        }>(`/api/accounting/trial-balance${query}`),
      ]);
      return { journal, balance };
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: JournalPage,
});

function money(amount: string): string {
  return Number(amount).toLocaleString("cs-CZ", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function JournalPage() {
  const { journal, balance } = Route.useLoaderData();
  const navigate = useNavigate();
  const router = useRouter();

  const seed = useApiMutation(() => api.post("/api/accounting/accounts/seed", {}), {
    onSuccess: () => router.invalidate(),
    errorMessage: () => "Couldn't create the chart of accounts.",
  });

  const reverse = useApiMutation(
    ({ id, reason }: { id: string; reason: string }) =>
      api.post(`/api/accounting/journal/${id}/reverse`, { reason }),
    { onSuccess: () => router.invalidate(), errorMessage: () => "The reversal failed." },
  );

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-semibold text-2xl">Journal {journal.year}</h1>
          <p className="text-muted-foreground text-sm">
            An entry is never edited or deleted. A correction is a reversal plus a new entry — both
            stay in the journal.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {journal.closed && <Badge variant="secondary">period closed</Badge>}
          <input
            type="number"
            defaultValue={journal.year}
            className="h-9 w-24 rounded-md border border-input bg-background px-2 text-sm"
            onChange={(e) =>
              navigate({
                to: "/accounting/journal",
                search: { year: Number(e.target.value) || undefined },
              })
            }
          />
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle>Trial balance</CardTitle>
            <CardDescription>
              Both sides have to add up — when they don't, there is no point looking at the
              statements.
            </CardDescription>
          </div>
          <Badge variant={balance.balanced ? "success" : "destructive"}>
            {balance.balanced ? "sides agree" : "sides disagree"}
          </Badge>
        </CardHeader>
        <CardContent>
          {balance.rows.length === 0 ? (
            <div className="space-y-3">
              <p className="text-muted-foreground text-sm">
                Nothing is posted for this year. If the chart of accounts is empty too, create it
                from the standard chart.
              </p>
              <Button variant="outline" disabled={seed.pending} onClick={() => seed.mutate()}>
                {seed.pending ? "Creating…" : "Create the chart of accounts and templates"}
              </Button>
              {seed.error && <p className="text-destructive text-sm">{seed.error}</p>}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1 pr-4 font-medium">Account</th>
                  <th className="py-1 pr-4 font-medium">Name</th>
                  <th className="py-1 pr-4 text-right font-medium">Debit</th>
                  <th className="py-1 pr-4 text-right font-medium">Dal</th>
                  <th className="py-1 text-right font-medium">Balance</th>
                </tr>
              </thead>
              <tbody>
                {balance.rows.map((row) => (
                  <tr key={row.code} className="border-t">
                    <td className="py-1 pr-4 font-mono text-xs">{row.code}</td>
                    <td className="py-1 pr-4">{row.name}</td>
                    <td className="py-1 pr-4 text-right tabular-nums">{money(row.debit)}</td>
                    <td className="py-1 pr-4 text-right tabular-nums">{money(row.credit)}</td>
                    <td className="py-1 text-right font-medium tabular-nums">
                      {money(row.balance)}
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 font-semibold">
                  <td className="py-1 pr-4" colSpan={2}>
                    Celkem
                  </td>
                  <td className="py-1 pr-4 text-right tabular-nums">{money(balance.totalDebit)}</td>
                  <td className="py-1 pr-4 text-right tabular-nums">
                    {money(balance.totalCredit)}
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Journal</CardTitle>
          <CardDescription>{journal.entries.length} entries</CardDescription>
        </CardHeader>
        <CardContent>
          {journal.entries.length === 0 ? (
            <p className="text-muted-foreground text-sm">Nothing posted yet.</p>
          ) : (
            <div className="space-y-3">
              {journal.entries.map((entry) => (
                <div key={entry.id} className="rounded-md border p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <span className="font-mono text-xs">no. {entry.number}</span>{" "}
                      <span className="text-muted-foreground text-xs">
                        <LocalDate value={entry.bookedOn} />
                      </span>{" "}
                      <span className="text-sm">{entry.description}</span>
                      {entry.reversesId && (
                        <Badge variant="outline" className="ml-2 text-[10px]">
                          storno
                        </Badge>
                      )}
                    </div>
                    {!entry.reversesId && (
                      <button
                        type="button"
                        className="text-muted-foreground text-xs underline"
                        disabled={reverse.pending}
                        onClick={() => {
                          const reason = window.prompt("Reason for the reversal:");
                          if (reason && reason.trim().length >= 2) {
                            reverse.mutate({ id: entry.id, reason: reason.trim() });
                          }
                        }}
                      >
                        stornovat
                      </button>
                    )}
                  </div>
                  <table className="mt-2 w-full text-sm">
                    <tbody>
                      {entry.lines.map((line) => (
                        <tr key={line.id}>
                          <td className="py-0.5 pr-4 font-mono text-xs">
                            {line.debitAccount.code} / {line.creditAccount.code}
                          </td>
                          <td className="py-0.5 pr-4 text-muted-foreground text-xs">
                            {line.description ??
                              `${line.debitAccount.name} → ${line.creditAccount.name}`}
                          </td>
                          <td className="py-0.5 text-right tabular-nums">{money(line.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
          {reverse.error && <p className="mt-2 text-destructive text-sm">{reverse.error}</p>}
        </CardContent>
      </Card>
    </div>
  );
}
